import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { getToken, formatApiErrorDetail } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { FileSignature, ShieldCheck, Loader2, Download, X, ExternalLink, CheckCircle2, XCircle } from "lucide-react";

// Client-facing retainer review + e-signature card (rendered on CaseDetail).
export default function RetainerSignCard({ caseId, clientName, onSigned }) {
  const [r, setR] = useState(undefined); // undefined=loading, null=none
  const [open, setOpen] = useState(false);
  const [sigData, setSigData] = useState("");
  const [sigType, setSigType] = useState("typed");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(`s_${Math.random().toString(36).slice(2, 10)}`);
  const bodyRef = useRef(null);
  const maxScroll = useRef(0);
  const trackingOn = useRef(false);

  const track = (event, extra = {}) => {
    if (!trackingOn.current) return;
    api.post(`/cases/${caseId}/retainer/track`, { event, session_id: sessionId.current, device: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, ...extra }).catch(() => {});
  };

  const load = async () => {
    try { const { data } = await api.get(`/cases/${caseId}/retainer`); setR(data.retainer || null); }
    catch { setR(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  // Live engagement tracking: open on mount (if pending), reading-time heartbeats, scroll %.
  useEffect(() => {
    if (!r || !["sent", "viewed"].includes(r.status)) return;
    trackingOn.current = true;
    track("open");
    const hb = setInterval(() => { if (document.visibilityState === "visible") track("heartbeat", { reading_seconds: 5 }); }, 5000);
    return () => { clearInterval(hb); trackingOn.current = false; };
    // eslint-disable-next-line
  }, [r?.retainer_id, r?.status]);

  const onScroll = () => {
    const el = bodyRef.current; if (!el) return;
    const pct = Math.min(100, Math.round(((el.scrollTop + el.clientHeight) / el.scrollHeight) * 100));
    if (pct > maxScroll.current + 4 || pct >= 100) { maxScroll.current = pct; track("scroll", { scroll_pct: pct }); }
  };

  if (r === undefined) return null;
  if (!r || ["revoked"].includes(r.status)) return null;

  const token = getToken();
  const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/retainer/preview?auth=${token}`;
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/retainer/download?auth=${token}`;

  const sign = async () => {
    if (!consent) return toast.error("Please confirm your consent to sign electronically.");
    if (!sigData) return toast.error("Please provide your signature.");
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/retainer/sign`, { type: sigType, name: clientName || "Client", signature_image: sigData, consent: true });
      setR(data.retainer); setOpen(false); toast.success("Retainer signed — your attorney has been notified.");
      onSigned?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    if (!window.confirm("Decline this retainer agreement?")) return;
    try { await api.post(`/cases/${caseId}/retainer/decline`, {}); toast.success("Retainer declined."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (r.status === "signed") {
    return (
      <div className="bg-emerald-50 rounded-3xl border border-emerald-100 p-6" data-testid="retainer-signed-card">
        <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /><h3 className="font-heading font-semibold text-navy">Retainer Signed</h3></div>
        <p className="text-sm text-slate-600">Signed on {new Date(r.signed_at).toLocaleString()}. Your attorney-client relationship is now active.</p>
        <a href={downloadUrl} data-testid="retainer-download-client" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-emerald-200 text-emerald-700 text-sm font-medium hover:bg-emerald-50"><Download className="w-4 h-4" /> Download signed PDF</a>
      </div>
    );
  }
  if (r.status === "declined") {
    return (
      <div className="bg-red-50 rounded-3xl border border-red-100 p-6" data-testid="retainer-declined-card">
        <div className="flex items-center gap-2"><XCircle className="w-5 h-5 text-red-500" /><h3 className="font-heading font-semibold text-navy">Retainer Declined</h3></div>
        <p className="text-sm text-slate-600 mt-1">You declined this retainer. Contact your attorney if this was a mistake.</p>
      </div>
    );
  }
  if (r.status === "expired") {
    return (
      <div className="bg-slate-50 rounded-3xl border border-slate-200 p-6" data-testid="retainer-expired-card">
        <div className="flex items-center gap-2"><XCircle className="w-5 h-5 text-slate-400" /><h3 className="font-heading font-semibold text-navy">Retainer Expired</h3></div>
        <p className="text-sm text-slate-600 mt-1">This retainer expired. Please ask your attorney to send a new one.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border-2 border-cobalt/30 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="retainer-sign-card">
      <div className="flex items-center gap-2 mb-1"><FileSignature className="w-5 h-5 text-cobalt" /><h3 className="font-heading font-semibold text-navy">Sign Your Retainer Agreement</h3></div>
      <p className="text-sm text-slate-500">{r.template_name || "Retainer"} · sent {new Date(r.sent_at).toLocaleDateString()}</p>

      {r.body_type === "pdf" ? (
        <a href={previewUrl} target="_blank" rel="noreferrer" data-testid="retainer-preview-pdf" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm hover:bg-slate-100"><ExternalLink className="w-4 h-4" /> Open agreement ({r.original_filename || "PDF"})</a>
      ) : (
        <div ref={bodyRef} onScroll={onScroll} className="mt-4 max-h-64 overflow-y-auto rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap" data-testid="retainer-body">{r.body_rendered}</div>
      )}

      <div className="mt-4 flex gap-2">
        <button onClick={() => { setOpen(true); setConsent(false); setSigData(""); track("reached_signature"); }} data-testid="retainer-open-sign" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover shadow-md active:scale-95"><FileSignature className="w-4 h-4" /> Review & Sign</button>
        <button onClick={decline} data-testid="retainer-decline" className="px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-600 font-medium hover:bg-slate-50">Decline</button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="retainer-sign-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-navy">Electronic Signature</h3>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <SignaturePad defaultName={clientName} onChange={(d, m) => { setSigData(d); if (m) setSigType(m); }} />
            <label className="mt-4 flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="retainer-consent" className="mt-0.5" />
              <span>I agree that my electronic signature is the legal equivalent of my handwritten signature and consent to be bound by this retainer agreement. My IP address and a timestamp will be recorded.</span>
            </label>
            <button onClick={sign} disabled={busy} data-testid="retainer-submit-sign" className="mt-4 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-cobalt text-white font-semibold hover:bg-cobalt-hover disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Sign Retainer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
